import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import FormTextField from "../../Form/FormTextField.jsx";
import FormSelect from "../../Form/FormSelect.jsx";
import FormDatePicker from "../../Form/FormDatePicker.jsx";
import FormNumberField from "../../Form/FormNumberField.jsx";
import FormFileUpload from "../../Form/FormFileUpload.jsx";
import FormSwitch from "../../Form/FormSwitch.jsx";
import DSButton from "../../../theme/components/Button.jsx";

import {
  COST_TYPE_OPTIONS,
  TYPE_FIELD_CONFIG,
  costEntrySchema,
  getAmountLabel,
  isAttachmentField,
} from "./costFieldSchemas.js";

/**
 * Form for creating or editing a single cost entry.
 * Dynamically renders fields based on the selected cost type.
 */
const CostEntryForm = ({ initialData, onSubmit, onCancel, isSubmitting }) => {
  // Helper to parse date strings to dayjs objects (required by MUI DatePicker)
  const parseDate = (dateValue) => {
    if (!dateValue) {
      return null;
    }
    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed : null;
  };

  // Transform data for form initialization
  // (convert plain anlage IDs to { id, filename, url } format)
  const transformDataForForm = (data, costType) => {
    if (!data) return {};
    const transformed = { ...data };
    Object.keys(transformed).forEach((key) => {
      const value = transformed[key];
      // Convert plain anlage ID numbers to the object format expected by FormFileUpload
      // Only for fields that are actually attachment fields for this cost type
      if (isAttachmentField(costType, key) && typeof value === "number") {
        transformed[key] = { id: value, filename: "Hochgeladene Datei" };
      }
    });
    return transformed;
  };

  const getDefaultValues = () => ({
    cost_type: initialData?.cost_type || "",
    amount: initialData?.amount ?? null,
    company_name: initialData?.company_name || "",
    invoice_number: initialData?.invoice_number || "",
    invoice_date: parseDate(initialData?.invoice_date),
    data: transformDataForForm(initialData?.data, initialData?.cost_type),
    is_assigned: initialData?.is_assigned ?? false,
  });

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(costEntrySchema),
  });

  const { handleSubmit, watch, reset } = methods;
  const selectedType = watch("cost_type");
  const isAssigned = watch("is_assigned");

  useEffect(() => {
    reset(getDefaultValues());
  }, [initialData, reset]);

  const handleButtonClick = async () => {
    // Manually trigger validation and submission without using a form element
    // This avoids nested form issues with the parent FormRenderer
    await handleSubmit(async (formData) => {
      // Format date for API (dayjs uses format(), not toISOString())
      const payload = {
        ...formData,
        invoice_date: formData.invoice_date
          ? dayjs(formData.invoice_date).format("YYYY-MM-DD")
          : null,
      };

      // Format data field values (dates and file uploads)
      if (payload.data) {
        Object.keys(payload.data).forEach((key) => {
          const value = payload.data[key];
          if (dayjs.isDayjs(value)) {
            payload.data[key] = value.format("YYYY-MM-DD");
          }
          // For attachment fields: keep full object in local state for display
          // Backend will extract just the ID for storage
        });
      }

      await onSubmit(payload);

      // Reset form to empty state after successful submission
      reset({
        cost_type: "",
        amount: null,
        company_name: "",
        invoice_number: "",
        invoice_date: null,
        data: {},
        is_assigned: false,
      });
    })();
  };

  const config = TYPE_FIELD_CONFIG[selectedType];
  const showFields = config?.showFields || [];
  const dataFields = config?.dataFields || [];
  const uploadFields = config?.uploadFields || [];
  const preCommonDataFields = dataFields
    .filter((field) => field?.order || 0 < 0)
    .sort((a, b) => a?.order || 0 - b?.order || 0);
  const postCommonDataFields = dataFields
    .filter((field) => field?.order || 0 >= 0)
    .sort((a, b) => a?.order || 0 - b.order || 0);
  const renderDataField = (field) => {
    const name = `data.${field.name}`;

    switch (field.type) {
      case "number":
        return (
          <FormNumberField
            key={field.name}
            name={name}
            label={field.label}
            unit={field.unit}
            fullWidth
          />
        );
      case "date":
        return (
          <FormDatePicker key={field.name} name={name} label={field.label} />
        );
      case "textarea":
        return (
          <FormTextField
            key={field.name}
            name={name}
            label={field.label}
            multiline
            rows={3}
            fullWidth
          />
        );
      default:
        return (
          <FormTextField
            key={field.name}
            name={name}
            label={field.label}
            fullWidth
          />
        );
    }
  };

  return (
    <FormProvider {...methods}>
      <Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormSelect
              name="cost_type"
              label="Kostenart"
              options={COST_TYPE_OPTIONS}
            />
          </Grid>

          {selectedType && (
            <>
              {preCommonDataFields.map((field) => (
                <Grid key={field.name} size={{ xs: 12, md: 6 }}>
                  {renderDataField(field)}
                </Grid>
              ))}

              {showFields.includes("company_name") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormTextField
                    name="company_name"
                    label="Name Firma"
                    fullWidth
                  />
                </Grid>
              )}

              {showFields.includes("invoice_number") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormTextField
                    name="invoice_number"
                    label="Rechnungsnummer"
                    fullWidth
                  />
                </Grid>
              )}

              {showFields.includes("invoice_date") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormDatePicker name="invoice_date" label="Rechnungsdatum" />
                </Grid>
              )}

              {showFields.includes("amount") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormNumberField
                    name="amount"
                    label={getAmountLabel(selectedType)}
                    unit="€"
                    fullWidth
                  />
                </Grid>
              )}

              {postCommonDataFields.map((field) => (
                <Grid key={field.name} size={{ xs: 12, md: 6 }}>
                  {renderDataField(field)}
                </Grid>
              ))}

              {uploadFields.length > 0 && (
                <Grid size={12}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {uploadFields.map((field) => (
                        <Grid key={field.name} size={{ xs: 12 }}>
                          <FormFileUpload
                            name={`data.${field.name}`}
                            label={field.label}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              )}

              <Grid size={12}>
                <FormSwitch
                  name="is_assigned"
                  label="Abtretungserklärung (Zahlung direkt vom Versicherer an Dienstleister)"
                />
              </Grid>

              {isAssigned && (
                <Grid size={12}>
                  <FormFileUpload
                    name="data.assignment_attachment"
                    label="Abtretungserklärung (Dokument)"
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <DSButton
            variant="tertiary"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Abbrechen
          </DSButton>
          <DSButton
            onClick={handleButtonClick}
            size="small"
            disabled={isSubmitting || !selectedType}
            loading={isSubmitting}
          >
            {initialData?.id ? "Übernehmen" : "Hinzufügen"}
          </DSButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

CostEntryForm.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export default CostEntryForm;
